import Modal from 'shared/Modal';
import { Button, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import DatePicker from 'components/DatePicker';
import { useState } from 'react';
import { format, sub } from 'date-fns';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { exportDeletedWorkPlanCsv } from 'thunk';
import { ExportDeletedWorkPlanCsvSuccessResponse } from 'TeamModule/types';
import { openUrl } from 'utils';

type ExportDeletedWorkPlanCsvModalProps = {
  selectedTeamId: number;
  closeModal: () => void;
  isOpen: boolean;
};

const stateId = 'deleted-work-plan-export';
const defaultDate = format(sub(new Date(), { months: 12 }), 'yyyy-MM-dd');

const ExportDeletedWorkPlanCsvModal = ({
  selectedTeamId,
  closeModal,
  isOpen,
}: ExportDeletedWorkPlanCsvModalProps) => {
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const { isRequesting, data: stateData } =
    selectLocalData<ExportDeletedWorkPlanCsvSuccessResponse>({
      stateId,
    });
  const [exportDeletedWorkPlanCsvOptions, setExportDeletedWorkPlanCsvOptions] = useState({
    startDate: defaultDate,
    excludeIntentional: false,
  });

  const generateExportDeletedWorkPlanCsvLink = () => {
    dispatchLocal(exportDeletedWorkPlanCsv, {
      params: {
        team_id: selectedTeamId,
        data_type: 'activity_phase_schedule_bar',
        date_limit: exportDeletedWorkPlanCsvOptions.startDate,
        exclude_intentional: exportDeletedWorkPlanCsvOptions.excludeIntentional,
        // Default is false, Indiciates whether or not to recover "intentionally" deleted work plans (There is a user activity corresponding to the delete). It is possible it was intentionally deleted as a result of e.g. a phase delete
      },
      stateId,
      onSuccessCallback: ({ response }) => {
        openUrl(response.data.url);
      },
      formatSuccessResponse: ({ response }) => {
        return response.data;
      },
    });
  };

  const handleStartDateChange = (newDate?: string | null) => {
    setExportDeletedWorkPlanCsvOptions((prev) => ({
      ...prev,
      startDate: newDate ?? '',
    }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setExportDeletedWorkPlanCsvOptions({
      ...exportDeletedWorkPlanCsvOptions,
      [name]: checked,
    });
  };

  const disableButton = !stateData || isRequesting;

  return (
    <Modal onClose={closeModal} open={isOpen} modalWidth={600}>
      <Box display={'flex'} gap="12px" flexDirection={'column'} padding={6}>
        <Typography variant="h3">
          <Box>Deleted Work Plan Export</Box>
        </Typography>
        <form>
          <Box padding={6} gap="12px">
            <DatePicker
              value={exportDeletedWorkPlanCsvOptions.startDate}
              label="Start Date"
              fullWidth
              onChange={handleStartDateChange}
            />
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportDeletedWorkPlanCsvOptions.excludeIntentional}
                  onChange={handleCheckboxChange}
                  name="excludeIntentional"
                  color="primary"
                />
              }
              label="Include Intentionally Deleted Work Plans"
            />
          </Box>
          <Box>
            <span>Indiciates whether or not to recover intentionally deleted work plans</span>
          </Box>
        </form>
        <Button
          variant="contained"
          color="primary"
          disabled={isRequesting}
          className="submit-button"
          onClick={generateExportDeletedWorkPlanCsvLink}
        >
          {isRequesting ? 'Please Wait' : 'Download File'}
        </Button>
      </Box>
    </Modal>
  );
};

export default ExportDeletedWorkPlanCsvModal;
